/**
 * Runtime Config Dialog
 *
 * Lists runtime config entries (files + env vars) for a feature at one
 * context level (global / pack / project) and lets an admin set/replace/
 * delete values. Secret values are write-only, non-secret ones prefilled (#127).
 * Editing an inherited entry forks a copy here; Revert drops an owned copy.
 */

import { useEffect, useState, type ReactElement } from 'react';
import {
  useRuntimeConfig,
  useUpsertRuntimeConfigValue,
  useDeleteRuntimeConfigValue,
} from '../../hooks/useRuntimeConfig';
import type { RuntimeConfigCtx } from '../../api/adminApi';
import { RuntimeConfigEntryItem } from './RuntimeConfigEntryItem';
import { RuntimeConfigSourceBanner } from './RuntimeConfigSourceBanner';
import { RuntimeConfigRevertButton } from './RuntimeConfigRevertButton';
import { RuntimeConfigAddControls } from './RuntimeConfigAddForms';
import { buildSavePayload, formatMutationError } from '../../utils/runtimeConfigHelpers';

interface RuntimeConfigDialogProps {
  open: boolean;
  featureId: string;
  featureName: string;
  projectId?: string;
  projectName?: string;
  packId?: string;
  packName?: string;
  onClose: () => void;
}

export function RuntimeConfigDialog({
  open,
  featureId,
  featureName,
  projectId,
  projectName,
  packId,
  packName,
  onClose,
}: RuntimeConfigDialogProps): ReactElement | null {
  const contextLevel: 'global' | 'pack' | 'project' = packId ? 'pack' : projectId ? 'project' : 'global';
  const ctx: RuntimeConfigCtx = packId ? { packId } : projectId ? { projectId } : {};
  const { data, isLoading, error: loadError } = useRuntimeConfig(open ? featureId : '', ctx);
  const upsert = useUpsertRuntimeConfigValue();
  const del = useDeleteRuntimeConfigValue();
  const [apiError, setApiError] = useState<string | null>(null);
  const [forkAcknowledged, setForkAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setApiError(null);
      setForkAcknowledged(false);
    }
  }, [open, featureId, projectId, packId]);

  const owned = data?.owned_by_context ?? true;
  // A revert hands ownership back up: the next edit must confirm a new fork.
  useEffect(() => { if (!owned) setForkAcknowledged(false); }, [owned]);
  if (!open) return null;
  const editable = owned || forkAcknowledged;

  const isRowPending = (kind: 'file' | 'env', name: string, mutation: typeof upsert | typeof del): boolean => {
    const vars = mutation.variables;
    return mutation.isPending && vars !== undefined && vars.kind === kind && vars.name === name;
  };

  // First save/add/delete while inherited forks a copy at this level
  // (server-side); this is just the UX confirmation before that happens.
  const ensureFork = (): boolean => {
    if (owned || forkAcknowledged) return true;
    const proceed = window.confirm(
      `This forks an independent copy of "${featureName}"'s config at the ${contextLevel} level. Continue?`,
    );
    if (proceed) setForkAcknowledged(true);
    return proceed;
  };

  // Callers (row / add-forms) clear their local input only once this resolves
  // — on failure, or a declined fork prompt, the input must survive to retry.
  const handleSave = async (
    kind: 'file' | 'env',
    name: string,
    payload: { value: string; target_path?: string; file_mode?: string; secret?: boolean },
  ): Promise<void> => {
    if (!ensureFork()) throw new Error('Fork not confirmed');
    setApiError(null);
    try {
      await upsert.mutateAsync({ featureId, kind, name, payload, ctx });
    } catch (err) {
      setApiError(formatMutationError('save', kind, name, err));
      throw err;
    }
  };

  const handleDelete = (kind: 'file' | 'env', name: string): void => {
    if (!ensureFork()) return;
    if (!window.confirm(`Delete stored value for "${name}"?`)) return;
    setApiError(null);
    del.mutate(
      { featureId, kind, name, ctx },
      { onError: (err) => { setApiError(formatMutationError('delete', kind, name, err)); } },
    );
  };

  const entries = data?.entries ?? [];
  const canRevert = Boolean(data?.owned_by_context) && contextLevel !== 'global';

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog dialog--wide"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={`Runtime config for ${featureName}`}
      >
        <h2>Runtime Config — {featureName}</h2>
        {data && (
          <RuntimeConfigSourceBanner
            contextLevel={contextLevel}
            ownedScope={data.owned_scope}
            ownedByContext={data.owned_by_context}
            {...(packName ? { packName } : {})}
            {...(projectName ? { projectName } : {})}
          />
        )}
        {apiError && <div className="dialog__error" role="alert">{apiError}</div>}
        {isLoading ? (
          <p>Loading…</p>
        ) : loadError ? (
          <p className="dialog__error">Failed to load runtime config.</p>
        ) : entries.length === 0 ? (
          <p className="project-features__empty">No runtime config entries defined for this feature.</p>
        ) : (
          <ul className="runtime-config__list">
            {entries.map((entry) => (
              <RuntimeConfigEntryItem
                key={`${entry.kind}:${entry.name}`}
                featureId={featureId}
                ctx={ctx}
                entry={entry}
                onSave={(value) => handleSave(entry.kind, entry.name, buildSavePayload(entry, value))}
                onDelete={() => { handleDelete(entry.kind, entry.name); }}
                onRequestEdit={() => { ensureFork(); }}
                isSaving={isRowPending(entry.kind, entry.name, upsert)}
                isDeleting={isRowPending(entry.kind, entry.name, del)}
                editable={editable}
                canDelete={editable && entry.is_set}
              />
            ))}
          </ul>
        )}
        <RuntimeConfigAddControls
          key={`${featureId}:${projectId ?? ''}:${packId ?? ''}`}
          isSaving={upsert.isPending}
          onAddFile={(name, payload) => handleSave('file', name, payload)}
          onAddEnv={(name, payload) => handleSave('env', name, payload)}
        />
        <div className="dialog__actions">
          {canRevert && (
            <RuntimeConfigRevertButton
              featureId={featureId}
              featureName={featureName}
              contextLevel={contextLevel}
              ctx={ctx}
              setApiError={setApiError}
            />
          )}
          <button type="button" className="dialog__btn dialog__btn--secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </dialog>
    </div>
  );
}
